import { Divider } from '@chakra-ui/react';
import { AxiosResponse, HttpStatusCode } from 'axios';
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAlertContext } from '../../../../../../../../../context/AlertProvider';
import { useApi } from '../../../../../../../../../context/ApiProvider';
import { useTenantAdminBasketContext } from '../../../../../../../../../context/TenantAdminBasketProvider';
import { ParentWithKidInterface } from '../../../../../../../../../shared/type/parent.type';
import calculatePrice from '../../../../../../../../../utils/calculators/calculatePrice';
import CustomChakraButton from '../../../../../../../../CustomChakraButton/CustomChakraButton';
import StickyNotes from '../../../../../../../../StickyNotes/StickyNotes';
import { useEditParentContext } from '../../../../../../../../../context/EditParentProvider';
import useAuth from '../../../../../../../../../hooks/useAuth';
import {
  useWaitingScreen,
  WaitingScreen
} from '../../../../../../../../ChildOwnerComponents/WaitingScreen/WaitingScreen';

interface Props {
  navigationSlot?: React.ReactNode;
}

const AdminCashOrderSummary = ({ navigationSlot }: Props) => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const location = useLocation();
  const { orderSummary, resetBasket } = useTenantAdminBasketContext();
  const { isOpen: showWaitingScreen, openWaitingScreen, closeWaitingScreen } = useWaitingScreen();

  const { apiTenantParentController, apiTenantAdminPaymentController } = useApi();
  const { setParentId, fetchListOfOrder } = useEditParentContext();
  const { id: tenantId, userId } = useParams<{ id: string; userId: string }>();
  const { setShow: setShowAlert, setAlertProperties } = useAlertContext();
  const isParentFlow = location.pathname.includes('parents');
  const isTeacherFlow = location.pathname.includes('teachers');
  const isEmployeeFlow = location.pathname.includes('employees');

  const singleUserTypePath = useMemo(() => {
    if (isParentFlow) {
      return 'parents/single-parent';
    }

    if (isTeacherFlow) {
      return 'teachers/single-teacher';
    }

    if (isEmployeeFlow) {
      return 'employees/single-employee';
    }
  }, [isParentFlow, isTeacherFlow, isEmployeeFlow]);

  const [parentWithKids, setParentWithKids] = useState<ParentWithKidInterface>();

  const findUserName = (consumerId: number): { firstName: string; lastName: string } => {
    const consumer = parentWithKids?.consumers.find((item) => item.id === consumerId);

    return {
      firstName: consumer?.firstName || '',
      lastName: consumer?.lastName || ''
    };
  };

  const fetchKids = async () => {
    try {
      const response: AxiosResponse<ParentWithKidInterface> = await apiTenantParentController(
        'details'
      ).get(`/${tenantId}/${userId}`);
      setParentWithKids(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  function wait(ms:number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  const confirmOrder = async () => {
    openWaitingScreen();
    try {
      const response = await apiTenantAdminPaymentController('confirm-cash-order').post('', {
        parentId: Number(userId),
        orderIds: orderSummary?.placedOrders.map((order) => order.id)
      });
      if (response.status === HttpStatusCode.Ok) {
        setAlertProperties({
          timeout: 9000,
          title: 'Sukces',
          description: 'Poprawnie złożono zamówienie',
          status: 'success'
        });
        resetBasket();
        await wait(300);// to make sure that history entry is created
        closeWaitingScreen();
        fetchListOfOrder();
        navigate(
          `/${auth.loggedUserDto.superAdmin ? 'superAdmin/branches' : 'tenantAdmin'}/id/${tenantId}/users/${singleUserTypePath}/${userId}/list-of-orders`
        );
      }
    } catch (error: any) {
      const responseError = error.response.data.error;
      closeWaitingScreen();
      setAlertProperties({
        timeout: 9000,
        title: 'Błąd',
        description: `Błąd składania zamówienia. ${responseError}`,
        status: 'error'
      });
    } finally {
      setShowAlert(true);
    }
  };

  useEffect(() => {
    if (isParentFlow && userId) {
      setParentId(Number(userId));
    }
  }, [isParentFlow, userId]);

  useEffect(() => {
    fetchKids();
  }, []);

  if (!orderSummary || !orderSummary.placedOrders) {
    return (
      <section className="w-full flex flex-col bg-grayLight-50">
        <div className="flex flex-col gap-4 pt-6 px-8 pb-6">
          {navigationSlot}
          <p className="text-2xl font-semibold text-grayLight-900">Podsumowanie zamówienia</p>
          <p className="text-sm text-grayLight-600">
            Sprawdź, czy ilość zamówionych posiłków i wybrane daty się zgadzają. Następnie przejdź
            do płatności.
          </p>
        </div>
        <div className="flex flex-grow pr-spacing-3xl pl-spacing-3xl gap-spacing-3xl pb-spacing-5xl">
          <div className="flex flex-col basis-4/6 rounded-2xl bg-white p-spacing-3xl gap-spacing-3xl">
            <p>Koszyk jest pusty.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full h-screen flex flex-col bg-grayLight-50 overflow-y-auto">
      <div className="flex flex-col gap-4 pt-6 px-8 pb-6">
        {navigationSlot}
        <p className="text-2xl font-semibold text-grayLight-900">Podsumowanie zamówienia</p>
        <p className="text-sm text-grayLight-600">
          Sprawdź, czy ilość zamówionych posiłków i wybrane daty się zgadzają. Następnie przejdź do
          płatności.
        </p>
      </div>

      <div className="flex px-8 gap-6 pb-10 flex-wrap lg:flex-nowrap">
        {/* Lewa kolumna */}
        <div className="flex flex-col flex-1 min-w-[60%] bg-white rounded-2xl p-6 gap-8">
          {orderSummary.placedOrders.length === 0 ? (
            <p>Koszyk jest pusty.</p>
          ) : (
            orderSummary.placedOrders.map((placedOrder) => (
              <div key={placedOrder.consumerId} className="flex flex-col gap-6">
                <div className="flex justify-between items-center border-b border-amber-300 py-4">
                  <p className="font-medium text-grayLight-700 text-base">
                    {findUserName(placedOrder.consumerId).firstName}{' '}
                    {findUserName(placedOrder.consumerId).lastName}
                  </p>
                  <p className="text-sm font-medium text-grayLight-700">
                    suma:{' '}
                    <span className="font-semibold text-grayLight-900">
                      {calculatePrice(placedOrder.creditPaymentCalculation?.basicToPay, null) ||
                        '0.00'}{' '}
                      zł
                    </span>
                  </p>
                </div>

                {(placedOrder.orderErrors || []).map((error, idx) => (
                  <StickyNotes key={idx} message={error} />
                ))}

                {(placedOrder.orderedDays || []).map((orderDay, idx) => {
                  const date = new Date(orderDay.forWhen);
                  const dayOfWeek = date.toLocaleDateString('pl-PL', { weekday: 'long' });
                  const capitalized = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);

                  return (
                    <div key={idx} className="flex flex-col gap-2">
                      <p className="text-sm text-grayLight-700">
                        {capitalized} ({orderDay.forWhen})
                      </p>
                      <div className="flex flex-col gap-2">
                        {orderDay.positions.map((pos, posIdx) => (
                          <React.Fragment key={posIdx}>
                            <div className="flex justify-between text-sm text-grayLight-900">
                              <span>
                                {pos.count}x {pos.name}
                              </span>
                              <span>{calculatePrice(pos.price, 'zł')}</span>
                            </div>
                            {posIdx < orderDay.positions.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Prawa kolumna */}
        <div className="flex flex-col justify-between w-full max-h-fit lg:w-1/3 bg-white rounded-2xl">
          <div className="flex flex-col gap-6 p-6">
            {orderSummary.placedOrders.map((order, index) => (
              <div key={index} className="flex flex-col gap-4">
                <p className="font-semibold text-lg text-grayLight-800">
                  {findUserName(order.consumerId).firstName}{' '}
                  {findUserName(order.consumerId).lastName}
                </p>

                <div className="flex justify-between text-sm text-grayLight-700">
                  <span>Wartość zamówienia:</span>
                  <span className="text-lg">
                    {calculatePrice(order.creditPaymentCalculation?.basicCost, null) || '0.00'} zł
                  </span>
                </div>

                <Divider />

                <div className="flex justify-between text-sm text-grayLight-700">
                  <span>Wpłata gotówką:</span>
                  <span className="text-lg">
                    {calculatePrice(order.creditPaymentCalculation?.basicCost, null) || '0.00'} zł
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-4 p-6 border-t border-gray-200">
            <div className="flex justify-between text-xl font-semibold text-grayLight-900">
              <span>Wartość zamówienia</span>
              <span>{calculatePrice(orderSummary.creditPaymentCalculation.basicToPay, 'zł')}</span>
            </div>

            <CustomChakraButton
              size="2xl"
              hierarchy="primary"
              iconPosition="onlyText"
              fullWidth
              buttonProps={{
                type: 'button',
                disabled: orderSummary.placedOrders.length === 0 || showWaitingScreen,
                onClick: confirmOrder
              }}>
              Potwierdź zamówienie i przyjmij wpłatę
            </CustomChakraButton>
          </div>
        </div>
      </div>

      <WaitingScreen isOpen={showWaitingScreen} onClose={() => null} />
    </section>
  );
};

export default AdminCashOrderSummary;
